import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputLabel,
  ListItemIcon,
  MenuItem,
  MenuList,
  useMediaQuery,
  Popover,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";

import { InlineSelectionContext, SelectionSheet } from "@/components/common/SelectionSheet";

export interface FilterMenuOption<T extends string> {
  label: string;
  value: T;
  icon?: React.ReactNode;
  showDividerAfter?: boolean;
}

export type FilterMenuControlHeight =
  number | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

export interface FilterMenuSummary {
  eyebrow: string;
  showSelectedOptionInEyebrow?: boolean;
  primaryText?: React.ReactNode;
  supportingText?: React.ReactNode;
}

interface FilterMenuButtonProps<T extends string> {
  selectedValue: T;
  options: FilterMenuOption<T>[];
  onChange: (value: T) => void;
  icon: React.ReactNode;
  showSelectedOptionIcon?: boolean;
  menuTitle?: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  compact?: boolean;
  fitContent?: boolean;
  controlHeight?: FilterMenuControlHeight;
  controlPaddingY?: number;
  menuMinWidth?: number;
  menuAlignment?: "start" | "end";
  minWidth?: { xs?: string; sm?: string; md?: string } | string;
  maxWidth?: { xs?: string; sm?: string; md?: string } | string;
  menuFooter?: React.ReactNode;
  renderMenuFooter?: (closeMenu: () => void) => React.ReactNode;
  showMenuFooterDivider?: boolean;
  shouldCloseOnChange?: (value: T) => boolean;
  summary?: FilterMenuSummary;
}

export const filterControlButtonStyles = {
  color: "text.primary",
  backgroundColor: "background.paper",
  border: 1,
  borderColor: "divider",
  textTransform: "none",
  px: 1.5,
  py: 1,
  borderRadius: 1,
  justifyContent: "flex-start",
  minHeight: 40,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "center",
  "&:hover": {
    backgroundColor: "action.hover",
  },
  "& .MuiButton-startIcon": {
    marginInlineEnd: 1,
    fontSize: "0.875rem",
    color: "text.primary",
  },
  "& .MuiButton-endIcon": {
    marginInlineStart: 1,
    marginInlineEnd: 0,
    color: "text.primary",
  },
  "&:not(.filter-menu-button--summary) .MuiTypography-root": {
    fontSize: "0.8125rem",
    whiteSpace: "nowrap",
  },
} as const;

const FilterButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  textTransform: "none",
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius,
  justifyContent: "flex-start",
  minHeight: 40,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "center",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "& .MuiButton-startIcon": {
    marginInlineEnd: theme.spacing(1),
    marginInlineStart: 0,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
  },
  "& .MuiButton-endIcon": {
    marginInlineStart: theme.spacing(1),
    marginInlineEnd: 0,
    color: theme.palette.text.primary,
  },
  "&:not(.filter-menu-button--summary) .MuiTypography-root": {
    fontSize: "0.8125rem",
    whiteSpace: "nowrap",
  },
}));

const FilterPopover = styled(Popover)(({ theme }) => ({
  "& .MuiPaper-root": {
    width: "var(--filter-width)",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "400px",
    overflowY: "auto",
    ...theme.unstable_sx({ borderRadius: 2 }),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[8],
  },
}));

export function FilterMenuButton<T extends string>({
  selectedValue,
  options,
  onChange,
  icon,
  showSelectedOptionIcon = false,
  menuTitle,
  label,
  disabled = false,
  loading = false,
  className,
  compact = false,
  fitContent = false,
  controlHeight,
  controlPaddingY,
  menuMinWidth = 250,
  menuAlignment = "end",
  minWidth = { xs: "unset", sm: "140px", md: "160px" },
  maxWidth = { md: "280px" },
  menuFooter,
  renderMenuFooter,
  showMenuFooterDivider = true,
  shouldCloseOnChange = () => true,
  summary,
}: FilterMenuButtonProps<T>) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const inlineSelection = React.useContext(InlineSelectionContext);
  const menuId = React.useId();
  const isRtl = theme.direction === "rtl";
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [buttonRef, setButtonRef] = React.useState<HTMLElement | null>(null);
  const [isButtonFocused, setIsButtonFocused] = React.useState(false);
  const closeMenu = () => {
    setAnchorEl(null);
    setIsButtonFocused(false);
    if (!isMobile) buttonRef?.blur();
    else if (inlineSelection) buttonRef?.focus();
  };
  const resolvedMenuFooter = renderMenuFooter?.(closeMenu) ?? menuFooter;

  const selectedOption = options.find((option) => option.value === selectedValue);
  const buttonIcon = showSelectedOptionIcon && selectedOption?.icon ? selectedOption.icon : icon;
  const isSummaryActive = Boolean(anchorEl) || isButtonFocused;
  const menuHorizontalOrigin =
    menuAlignment === "start" ? (isRtl ? "right" : "left") : isRtl ? "left" : "right";

  const trigger = (
    <FilterButton
      ref={setButtonRef}
      variant="outlined"
      size="small"
      startIcon={summary ? undefined : buttonIcon}
      endIcon={
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          {loading ? <CircularProgress size={18} /> : null}
          {summary ? (
            <IconButton
              aria-hidden="true"
              className="filter-menu-button__arrow"
              component="span"
              disableFocusRipple
              tabIndex={-1}
            >
              <ArrowDropDownIcon sx={{ transform: anchorEl ? "rotate(180deg)" : undefined }} />
            </IconButton>
          ) : (
            <ArrowDropDownIcon sx={{ transform: anchorEl ? "rotate(180deg)" : undefined }} />
          )}
        </Box>
      }
      onClick={(event) => setAnchorEl(event.currentTarget)}
      onFocus={() => setIsButtonFocused(true)}
      onBlur={() => setIsButtonFocused(false)}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-haspopup={isMobile && !inlineSelection ? "dialog" : "menu"}
      aria-expanded={Boolean(anchorEl)}
      aria-controls={anchorEl ? menuId : undefined}
      disableRipple={Boolean(summary)}
      disableFocusRipple={Boolean(summary)}
      className={[className, summary && "filter-menu-button--summary"].filter(Boolean).join(" ")}
      sx={{
        color: "text.primary",
        minWidth: summary ? 0 : minWidth,
        maxWidth: summary ? "none" : maxWidth,
        width: summary ? "100%" : undefined,
        flex: summary ? "none" : "1 0 auto",
        ...(summary && {
          alignItems: "center",
          backgroundColor: "transparent",
          border: 0,
          borderRadius: 0,
          px: 1.5,
          // The autocomplete's popup indicator sits 3px closer to the logical
          // trailing edge than MUI's Button end-icon slot.
          paddingInlineEnd: 1.125,
          py: 1.25,
          "&:hover, &:active": { backgroundColor: "transparent" },
          "&.Mui-disabled": { border: 0 },
          "& .MuiButton-endIcon": {
            alignSelf: "center",
            alignItems: "center",
            display: "inline-flex",
            height: 28,
            marginInlineEnd: 0,
            marginInlineStart: 1,
            justifyContent: "center",
            minWidth: 28,
            "& .MuiSvgIcon-root": { fontSize: 24 },
          },
          "& .filter-menu-button__arrow": {
            height: 28,
            // Align the arrow's trailing edge with the 12px inset used by
            // MuiAutocompleteMultiSelect's end adornment.
            padding: 0,
            width: 28,
          },
        }),
        ...(disabled && {
          backgroundColor: "transparent",
          borderColor: "transparent",
          color: "text.disabled",
          opacity: 1,
          "& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiTypography-root, & .MuiSvgIcon-root":
            {
              color: "text.disabled",
            },
        }),
        ...(compact && {
          minHeight: 32,
          py: 0.25,
        }),
        ...(fitContent &&
          !summary && {
            flex: { md: "0 0 auto" },
            width: { md: "fit-content" },
          }),
        ...(controlHeight !== undefined && {
          height: controlHeight,
          minHeight: controlHeight,
          maxHeight: controlHeight,
          py: 0.5,
        }),
        ...(controlPaddingY !== undefined && { py: controlPaddingY }),
      }}
    >
      {summary ? (
        // TODO: Refactor to a MUI-native outlined control instead of mirroring its outline and label geometry here.
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 0.5,
            minWidth: 0,
            width: "100%",
          }}
        >
          <Typography
            component="span"
            sx={{
              color: "text.primary",
              fontSize: "1rem",
              fontWeight: 700,
              lineHeight: 1.35,
              overflow: "hidden",
              textAlign: "start",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {summary.primaryText ?? selectedOption?.label ?? ""}
          </Typography>
          {summary.supportingText && !summary.showSelectedOptionInEyebrow ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "text.secondary",
                fontSize: "0.8125rem",
                gap: 0.5,
                lineHeight: 1.35,
                overflow: "hidden",
                textAlign: "start",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {summary.supportingText}
            </Box>
          ) : null}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "100%",
            flexWrap: "nowrap",
          }}
        >
          {label ? (
            <>
              <Typography component="span" sx={{ color: "text.primary", flexShrink: 0 }}>
                {label}
              </Typography>
              <Typography component="span" sx={{ mx: 1, color: "text.secondary", flexShrink: 0 }}>
                |
              </Typography>
            </>
          ) : null}
          <Typography
            component="span"
            sx={{
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              flexGrow: 1,
              textAlign: "start",
            }}
          >
            {selectedOption?.label ?? ""}
          </Typography>
        </Box>
      )}
    </FilterButton>
  );

  const menuContent = (
    <Box sx={{ width: "100%" }}>
      {menuTitle && !isMobile ? (
        <Typography
          variant="subtitle2"
          sx={{
            px: 2,
            py: 1,
            fontSize: "1rem",
            fontWeight: "medium",
            borderBottom: "1px solid",
            borderColor: "divider",
            textAlign: "start",
          }}
        >
          {menuTitle}
        </Typography>
      ) : null}
      <MenuList id={isMobile && !inlineSelection ? undefined : menuId} sx={{ p: 1 }}>
        {options.map((option) => (
          <React.Fragment key={option.value}>
            <MenuItem
              dense
              onClick={() => {
                onChange(option.value);
                if (shouldCloseOnChange(option.value)) {
                  closeMenu();
                }
              }}
              selected={selectedValue === option.value}
              sx={{
                minHeight: { xs: 48, md: 32 },
                whiteSpace: "normal",
                color:
                  selectedValue === option.value
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
                "&.Mui-selected:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                },
              }}
            >
              {option.icon ? (
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: 32,
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                >
                  {option.icon}
                </ListItemIcon>
              ) : null}
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.label}
              </Typography>
            </MenuItem>
            {option.showDividerAfter ? <Divider sx={{ my: 0.5 }} /> : null}
          </React.Fragment>
        ))}
      </MenuList>
      {!isMobile && resolvedMenuFooter ? (
        <>
          {showMenuFooterDivider ? <Divider /> : null}
          <Box sx={{ p: 1.5 }}>{resolvedMenuFooter}</Box>
        </>
      ) : null}
    </Box>
  );

  return (
    <>
      {summary ? (
        <Box
          sx={{
            borderRadius: 1,
            boxSizing: "border-box",
            display: "block",
            flex: "1 0 auto",
            margin: 0,
            maxWidth,
            minWidth,
            padding: 0,
            position: "relative",
            ...(!disabled &&
              !isSummaryActive && {
                "&:hover .filter-menu-button__outline": { borderColor: "text.primary" },
              }),
            ...(fitContent && {
              flex: { md: "0 0 auto" },
              width: { md: "fit-content" },
            }),
          }}
        >
          <Box
            aria-hidden="true"
            className="filter-menu-button__outline"
            component="fieldset"
            sx={{
              border: "1px solid",
              borderColor: disabled
                ? "action.disabled"
                : isSummaryActive
                  ? "primary.main"
                  : "rgba(0, 0, 0, 0.23)",
              borderRadius: "inherit",
              bottom: 0,
              insetInline: 0,
              margin: 0,
              minWidth: 0,
              overflow: "hidden",
              paddingInline: 1,
              pointerEvents: "none",
              position: "absolute",
              top: -5,
              transition: (currentTheme) =>
                currentTheme.transitions.create("border-color", {
                  duration: currentTheme.transitions.duration.shorter,
                }),
            }}
          >
            <Box
              component="legend"
              sx={{
                display: "block",
                fontSize: "0.75em",
                height: 11,
                marginInlineEnd: "auto",
                maxWidth: "100%",
                overflow: "hidden",
                padding: 0,
                visibility: "hidden",
                whiteSpace: "nowrap",
                "& > span": {
                  display: "inline-block",
                  opacity: 0,
                  paddingInline: 0.625,
                  visibility: "visible",
                },
              }}
            >
              <span>{summary.eyebrow}</span>
            </Box>
          </Box>
          <InputLabel
            shrink
            variant="outlined"
            sx={{
              color: disabled
                ? "action.disabled"
                : isSummaryActive
                  ? "primary.main"
                  : "text.secondary",
              insetInlineStart: 0,
              pointerEvents: "none",
              position: "absolute",
              top: 0,
              transform: "translate(14px, -9px) scale(0.75)",
              transformOrigin: "top left",
              zIndex: 1,
            }}
          >
            {summary.eyebrow}
          </InputLabel>
          {trigger}
        </Box>
      ) : (
        trigger
      )}

      {isMobile ? (
        inlineSelection ? (
          Boolean(anchorEl) && (
            <Box sx={{ mt: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
              {menuContent}
              {resolvedMenuFooter && <Box sx={{ p: 2 }}>{resolvedMenuFooter}</Box>}
            </Box>
          )
        ) : (
          <SelectionSheet
            open={Boolean(anchorEl)}
            title={menuTitle ?? label ?? summary?.eyebrow ?? t("common.multiSelect.fields.select")}
            id={menuId}
            onClose={closeMenu}
            footer={resolvedMenuFooter}
          >
            {menuContent}
          </SelectionSheet>
        )
      ) : (
        <FilterPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={closeMenu}
          disableRestoreFocus
          anchorOrigin={{
            vertical: "bottom",
            horizontal: menuHorizontalOrigin,
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: menuHorizontalOrigin,
          }}
          sx={{
            "--filter-width": buttonRef?.offsetWidth
              ? `${Math.max(buttonRef.offsetWidth, menuMinWidth)}px`
              : `${menuMinWidth}px`,
            ...(summary && {
              "& .MuiPaper-root": { mt: 1 },
            }),
          }}
        >
          {menuContent}
        </FilterPopover>
      )}
    </>
  );
}
